import { Add, CheckCircleOutline, PollOutlined, UploadOutlined, EditOutlined } from '@mui/icons-material'
import { Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { FunctionComponent, SetStateAction, useContext } from 'react';
import { useMediaQuery } from '@mui/material';
import gaiaTheme from '../../../../../root-components/theme';
import { Link } from 'react-router-dom';
import { IGroupDetail } from '../../../../../shared/services/group/group.model';
import { useHasOpenReportPeriod } from '../../../../../hooks/use-has-open-report-period';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { CustomFilter } from '../../../miscellaneous-components/custom-filter';
import { emissionsFilterConfig } from '../../../miscellaneous-components/custom-filter-config';

export const EmissionsGroupButtons: FunctionComponent<{
  status: 'outstanding' | 'completed' | 'upcoming',
  setStatus: React.Dispatch<SetStateAction<any>>,
  showCategoriesHandler: () => void,
  showToggle?: boolean,
  group: IGroupDetail,
  outstandingActions: number,
  completeAllActionsHandler: () => void,
  doesGroupHaveActions: boolean,
  isTasksTab?: boolean,
  selected?: string[],
  onBulkEditClick?: () => void,
  actionsOnly?: boolean,
}> = ({
  status,
  setStatus,
  showCategoriesHandler,
  showToggle = false,
  group,
  outstandingActions,
  completeAllActionsHandler,
  doesGroupHaveActions,
  isTasksTab = false,
  selected,
  onBulkEditClick,
  actionsOnly = false,
}) => {
    //Hooks 
    const hasOpenReport = useHasOpenReportPeriod([]);
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));


    const statusHandler = (e: React.MouseEvent<HTMLElement>, newStatus: 'outstanding' | 'completed' | 'upcoming' | null) => {
      if (newStatus !== null) {
        setStatus(newStatus)
      }
    }

    const filterConfig = emissionsFilterConfig;

    const handleFilterChange = (filters: Record<string, any>) => {
      console.log('Filter values:', filters);
    };

    const actionButtons = (
      <Stack display='flex' flexDirection='row' alignItems='center' justifyContent='flex-end' gap={1} sx={{ width: actionsOnly ? 'auto' : '100%' }}>
        {/* <Stack mr={1}>
          <CustomFilter
            filters={filterConfig}
            onFilterChange={handleFilterChange}
            buttonAlignment="right"
          />
        </Stack> */}
        {group.id === '4c052dd4-53a3-405b-90df-023cb2385a01' &&
          <Button variant='outlined' component={Link} to='/surveys'>
            <PollOutlined color='primary' />
            <Typography sx={{ color: 'primary.main', display: 'inline' }}>Go to Surveys</Typography>
          </Button>
        }
        {/* Hidden rather than disabled when there is nothing outstanding: a greyed-out bulk
            button with nothing to act on is just noise. */}
        {doesGroupHaveActions && status === 'outstanding' && isTasksTab && outstandingActions > 0 &&
          <Button variant='contained' onClick={completeAllActionsHandler}>
            <CheckCircleOutline />
            <Typography>Complete Tasks</Typography>
          </Button>
        }
        {onBulkEditClick && (
          <Button
            variant="contained"
            onClick={onBulkEditClick}
            disabled={!selected || selected.length === 0}
            sx={{ minWidth: 100 }}
            startIcon={<EditOutlined />}
          >
            <Typography>
              Edit Selected ({selected?.length || 0})
            </Typography>
          </Button>
        )}
        {/* Adding an emission is the page's primary action, so it gets the contained variant.
            Rendered only once the open-period check has resolved: a contained button painted
            disabled first and enabled a moment later reads as a glitch. */}
        {hasOpenReport !== null &&
          <Button variant='contained' onClick={showCategoriesHandler} disabled={!hasOpenReport} sx={{ ml: 1 }}>
            <Add />
            <Typography>Add Emission</Typography>
          </Button>}
        {/* Bulk upload applies to every group, not just the two it originally launched on.
            Admin or analyst: the BE file-upload routes are hasRequiredRole(['admin', 'analyst'])
            since the ux-be-followups role widening, so both roles can run the import wizard. */}
        {(userDetails?.roles.includes('admin') || userDetails?.roles.includes('analyst')) &&
          <Button variant='text' component={Link} to={`/settings/data-upload/import-data`} disabled={!hasOpenReport} sx={{ ml: 1 }}>
            <UploadOutlined sx={{ color: !hasOpenReport ? 'text.secondary' : 'primary.main' }} />
            <Typography sx={{ color: !hasOpenReport ? 'text.secondary' : 'primary.main' }} >Upload data</Typography>
          </Button>
        }
      </Stack>
    )

    if (actionsOnly) {
      return actionButtons
    }

    return (
      <Stack display='flex' flexDirection={isMobileMode ? 'column' : 'row'} alignItems='start' justifyContent='space-between' sx={{ mb: 2 }}>
        <Stack sx={{ width: '100%' }}>
          {showToggle && <ToggleButtonGroup value={status} exclusive onChange={statusHandler} sx={{ mb: 2 }} orientation={isMobileMode ? "vertical" : 'horizontal'}>
            <ToggleButton value='outstanding' sx={{ pt: 0.75, pb: 0.75, width: !isMobileMode ? '175px' : '100%', }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Outstanding tasks</Typography></ToggleButton>
            <ToggleButton value='upcoming' sx={{ pt: 0.75, pb: 0.75, width: !isMobileMode ? '175px' : '100%', }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Upcoming tasks</Typography></ToggleButton>
            <ToggleButton value='completed' sx={{ pt: 0.75, pb: 0.75, width: !isMobileMode ? '175px' : '100%', }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Completed tasks</Typography></ToggleButton>
          </ToggleButtonGroup>}
        </Stack>
        {actionButtons}
      </Stack>
    )
  }